import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";

import * as api from "../api.js";
import {
  PageHeader,
  Loading,
  ErrorBanner,
  PromptEditor,
  VersionHistory,
  Tabs,
  StatusBadge,
  VersionBadge,
} from "../components.jsx";
import ProtocolLinker from "./ProtocolLinker.jsx";
import { useLibraryEnv, useEnvPrefix } from "../LibraryEnvContext.jsx";

export default function LibrarySpecialtyAgent() {
  const { specialty, agent } = useParams();
  const { env, isLive } = useLibraryEnv();
  const prefix = useEnvPrefix();
  const [tab, setTab] = useState("editor");
  const [doc, setDoc] = useState(null);
  const [history, setHistory] = useState([]);
  const [linkedProtocols, setLinkedProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [docRes, histRes, linksRes] = await Promise.all([
        api.getSpecialtyAgent(specialty, agent, env),
        api.getSpecialtyAgentHistory(specialty, agent),
        api.getSpecialtyAgentLinkedProtocols(specialty, agent),
      ]);
      setDoc(docRes.data);
      setHistory(histRes.data || []);
      setLinkedProtocols(linksRes.data?.linked_protocols || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [specialty, agent, env]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaveLinkedProtocols = async (links) => {
    setBusy(true);
    setError(null);
    try {
      await api.setSpecialtyAgentLinkedProtocols(specialty, agent, links);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async (content) => {
    setBusy(true);
    setError(null);
    try {
      await api.writeSpecialtyAgent(specialty, agent, {
        content,
        mode: "draft",
        base_version: doc?.version ?? null,
        author: "admin",
        commit_msg: "Edit from admin UI",
      });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={`${specialty} / ${agent}`}
        subtitle={
          <>
            Specialty template — starting point for forked sub-tenants.{" "}
            <strong>Editing here does NOT affect existing sub-tenants</strong> (they
            own their own copies).
          </>
        }
        breadcrumbs={[
          { label: "Library", to: `${prefix}/platform` },
          { label: "Specialty Templates", to: `${prefix}/specialties` },
          { label: specialty, to: `${prefix}/specialties/${specialty}` },
          { label: agent },
        ]}
        actions={
          doc && (
            <>
              <VersionBadge version={doc.version} />
              <StatusBadge doc={doc} />
            </>
          )
        }
      />

      <Tabs
        tabs={[
          { id: "editor", label: "Editor" },
          { id: "protocols", label: `Agent Rules (${linkedProtocols.length})` },
          { id: "history", label: `History (${history.length})` },
        ]}
        active={tab}
        onChange={setTab}
      />

      <div className="tab-content">
      <ErrorBanner error={error} />

      {loading ? (
        <Loading />
      ) : tab === "editor" ? (
        doc ? (
          <PromptEditor
            initialContent={doc.content}
            onSave={handleSave}
            busy={busy}
            draftDoc={doc}
            readOnly={isLive}
            hidePublish
          />
        ) : (
          <div className="empty-state">
            <h3>Not found</h3>
          </div>
        )
      ) : tab === "protocols" ? (
        <>
          <p className="muted small mb-16">
            Protocols linked at the specialty-template level become the DEFAULT
            for new sub-tenants forked from this specialty. Existing sub-tenants
            keep their own sub-tenant-level assignments (edit those from the
            sub-tenant's agent page).
          </p>
          <ProtocolLinker
            specialty={specialty}
            currentLinks={linkedProtocols}
            onSave={handleSaveLinkedProtocols}
            busy={busy}
          />
        </>
      ) : (
        <div className="card">
          <VersionHistory versions={history} />
        </div>
      )}
      </div>
    </>
  );
}
